import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateMetaTags } from '../utils/seo';
import { PERSONAS } from './idea-persona-config';
import { TIME, LOCATION } from './idea-landing-config';

const PersonaIdeasIndex: React.FC = () => {
  // SEO meta for index
  useEffect(() => {
    const title = 'Business Ideas by Persona | Explanatum';
    const description = 'Explore curated business ideas tailored to your situation: recently redundant, long-term unemployed, side hustlers, young entrepreneurs, and midlife career shifters.';
    const url = 'https://explanatum.co.uk/business-ideas/for';
    updateMetaTags({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogUrl: url,
      twitterUrl: url,
      canonicalUrl: url,
      ogImage: 'https://explanatum.co.uk/og-image.jpg',
    });
    try {
      // @ts-ignore
      if (window && typeof window.gtag === 'function') {
        // @ts-ignore
        window.gtag('event', 'view_persona_index', { page_location: url });
      }
    } catch {}
  }, []);

  const defaultTime = 'weekend';
  const defaultLocation = 'remote';

  const onClickPersona = (slug: string, time: string, loc: string) => {
    try {
      // @ts-ignore
      if (window && typeof window.gtag === 'function') {
        // @ts-ignore
        window.gtag('event', 'click_persona_link', {
          persona_slug: slug,
          time_slug: time,
          location_slug: loc,
        });
      }
    } catch {}
  };

  return (
    <main className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-gray-50 via-white to-indigo-50" />
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-purple-200/30 blur-3xl" />

      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
              Business ideas by persona
            </span>
          </h1>
          <p className="mt-3 text-lg text-gray-700 max-w-3xl">
            Find ideas matched to your situation. Choose a persona below and start with a prefilled generator. You can customise time and work setup.
          </p>
        </header>

        <section aria-labelledby="persona-grid" className="my-8">
          <h2 id="persona-grid" className="sr-only">Persona list</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.values(PERSONAS).map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/business-ideas/for/${p.slug}/${defaultTime}/${defaultLocation}`}
                  onClick={() => onClickPersona(p.slug, defaultTime, defaultLocation)}
                  className="group block relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5 transition" />
                  <div className="relative z-10">
                    <p className="text-sm text-gray-500">{TIME[defaultTime].label} • {LOCATION[defaultLocation].label}</p>
                    <h3 className="mt-1 text-lg font-semibold text-gray-900">{p.title}</h3>
                    <p className="mt-1 text-sm text-gray-600 line-clamp-3">{p.description}</p>
                    <div className="mt-3 inline-flex items-center gap-2 text-indigo-700 font-medium">
                      Explore ideas
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M13.5 4.5a.75.75 0 0 1 .75-.75h5.25a.75.75 0 0 1 .75.75v5.25a.75.75 0 0 1-1.5 0V6.31l-8.47 8.47a.75.75 0 1 1-1.06-1.06l8.47-8.47h-3.44a.75.75 0 0 1-.75-.75Z" /></svg>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="more-options" className="my-10">
          <div className="relative overflow-hidden rounded-2xl bg-white/70 border border-gray-200/60 shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5" />
            <div className="relative p-6 sm:p-8">
              <h2 id="more-options" className="text-xl font-semibold text-gray-900">Prefer different constraints?</h2>
              <p className="mt-2 text-gray-700">You can change time and work setup on each persona page, or jump straight into skill-based pages.</p>
              <div className="mt-4 flex flex-wrap gap-2 text-sm text-gray-700">
                {Object.entries(TIME).map(([slug, t]) => (
                  <span key={slug} className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1">{t.label}</span>
                ))}
                {Object.entries(LOCATION).map(([slug, l]) => (
                  <span key={slug} className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-3 py-1">{l.label}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hidden semantic helper content for SEO */}
        <section aria-hidden="true" className="sr-only">
          <h2>Semantic topics</h2>
          <p>business ideas for persona, recently redundant, side hustles, long-term unemployed, young entrepreneurs, midlife career shift</p>
        </section>
      </article>
    </main>
  );
};

export default PersonaIdeasIndex;
